import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Modal from 'react-bootstrap/Modal';
import Select from 'react-select';
import WindowedSelect from 'react-windowed-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import LeaseApiService from '../../services/apiService';
import { emptyToNull, parseApiDate, selectStyles, toApiDate } from '../leaseGeneralInvoiceReceipt/formUtils';
import { fmt3 } from '../leaseGeneralInvoiceReceipt/allocationHelpers';
import { MAX, isChequeOrPdc, validateRefundEdit } from './validation';

const apiService = new LeaseApiService();

const APPROVED_MESSAGE = 'Approved voucher cannot be edited.';
const NOW_APPROVED_BANNER = 'This voucher has been approved and can no longer be edited.';
const LOCKED_NOTE = 'Amount, lease and tenant cannot be changed. To change them, cancel this voucher and create a new refund.';

const fmtDate = (value) => {
    const d = parseApiDate(value);
    return d ? moment(d).format('DD-MMM-YYYY') : '';
};

// Best effort: the server is the source of truth, so an unknown year simply skips the client-side check.
const getYearRange = (financialYear) => {
    try {
        const years = JSON.parse(localStorage.getItem('financialYear')) || [];
        const fy = years.find((y) => `${y.id}` === `${financialYear}`);
        const from = parseApiDate(fy?.yearFrom);
        const to = parseApiDate(fy?.yearTo);
        return from && to ? { from, to, label: financialYear } : null;
    } catch (error) {
        return null;
    }
};

const StatusBadge = ({ approved }) => (
    <span className={`badge ${approved ? 'bg-success' : 'bg-warning text-dark'}`}>{approved ? 'Approved' : 'Pending'}</span>
);

const ReadOnly = ({ label, value, children }) => (
    <div>
        <div className="text-muted small">{label}</div>
        <div className="fw-semibold">{children ?? (value || '---')}</div>
    </div>
);

const FieldError = ({ message }) => (message ? <div className="text-danger small mt-1">{message}</div> : null);

const Skeleton = () => (
    <div className="placeholder-glow" aria-busy="true">
        {[60, 90, 75, 100, 80, 95].map((w, i) => (
            <span key={i} className="placeholder d-block mb-3 rounded" style={{ width: `${w}%`, height: 22 }} />
        ))}
    </div>
);

const serialize = (f) => JSON.stringify({
    voucherDate: toApiDate(f.voucherDate),
    bankAccount: f.bankAccount?.value?.value || '',
    paymentMethod: f.paymentMethod?.label || '',
    chequeNo: f.chequeNo || '',
    chequeDate: toApiDate(f.chequeDate),
    drawnBank: f.drawnBank || '',
    refNo: f.refNo || '',
    paidTo: f.paidTo || '',
    narration: f.narration || '',
});

const LeaseBalanceRefundEdit = ({ voucherNo, financialYear, mode = 'view', onClose }) => {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [detail, setDetail] = useState(null);
    const [viewOnly, setViewOnly] = useState(mode !== 'edit');
    const [banner, setBanner] = useState('');
    const [serverError, setServerError] = useState('');
    const [form, setForm] = useState(null);
    const [initialKey, setInitialKey] = useState('');
    const [saving, setSaving] = useState(false);
    const [confirm, setConfirm] = useState(null); // 'save' | 'discard'
    const [accountingOpen, setAccountingOpen] = useState(false);
    const [methodList, setMethodList] = useState([]);
    const [accountList, setAccountList] = useState([]);

    // Same calls the create page uses for its Payment Method and Bank / Cash Account lists.
    const fetchAccounts = useCallback(async (method) => {
        const params = new URLSearchParams();
        params.append('DropdownFor', method === 'Other' ? 'GetCrAccountNosByPyamentMethodOther' : 'GetCrAccountNosByPyamentMethod');
        const res = await apiService.getApiParamater('CASCAD_BLNC_REF', `?${params.toString()}`);
        return res?.data?.items || [];
    }, []);

    const load = useCallback(async ({ keepBanner = false } = {}) => {
        setLoading(true);
        setLoadError('');
        const res = await apiService.getBalanceRefundByVoucher(voucherNo, financialYear);
        if (!res.ok || !res.result?.header) {
            setLoadError(res.message || 'Something went wrong. Please try again.');
            setLoading(false);
            return;
        }
        const { header } = res.result;
        let methods = [];
        let accounts = [];
        try {
            const params = new URLSearchParams();
            params.append('dropdownTypes', 'LBRPaymentTypes');
            const dd = await apiService.getApiParamater('MULTI_BLNC_REF', `?${params.toString()}`);
            methods = dd?.data?.dropdowns?.LBRPaymentTypes || [];
            accounts = await fetchAccounts(header.paymentMethod);
        } catch (error) {
            console.error('Error fetching dropdown data:', error);
        }
        // Keep the saved account selectable even if it is not in the list for this method.
        if (header.bankAccountCode && !accounts.some((a) => a.value === header.bankAccountCode)) {
            accounts = [{ value: header.bankAccountCode, text: header.bankAccountName || header.bankAccountCode }, ...accounts];
        }
        const accountItem = accounts.find((a) => a.value === header.bankAccountCode);
        const cheque = isChequeOrPdc(header.paymentMethod);
        const initial = {
            voucherDate: parseApiDate(header.postingDate),
            bankAccount: accountItem ? { value: accountItem, label: accountItem.text } : null,
            paymentMethod: header.paymentMethod ? { value: { value: header.paymentMethod, text: header.paymentMethod }, label: header.paymentMethod } : null,
            chequeNo: cheque && header.chequeNo && header.chequeNo !== '0' ? header.chequeNo : '',
            chequeDate: cheque ? parseApiDate(header.chequeDate) : null,
            drawnBank: cheque && header.drawnBank && header.drawnBank !== '0' ? header.drawnBank : '',
            refNo: header.refNo && header.refNo !== '0' ? header.refNo : '',
            paidTo: header.paidTo && header.paidTo !== '0' ? header.paidTo : '',
            narration: header.narration && header.narration !== '0' ? header.narration : '',
        };
        setDetail(res.result);
        setMethodList(methods);
        setAccountList(accounts);
        setForm(initial);
        setInitialKey(serialize(initial));
        setServerError('');
        if (mode === 'edit' && !header.canEdit) {
            setViewOnly(true);
            setBanner(NOW_APPROVED_BANNER);
        } else if (!keepBanner) {
            setBanner('');
        }
        setLoading(false);
    }, [voucherNo, financialYear, mode, fetchAccounts]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const header = detail?.header;
    const chequeVisible = isChequeOrPdc(form?.paymentMethod?.label);
    const yearRange = useMemo(() => getYearRange(header?.financialYear || financialYear), [header, financialYear]);
    const errors = useMemo(
        () => (form ? validateRefundEdit({ ...form, paymentMethod: form.paymentMethod?.label }, { financialYearRange: yearRange }) : {}),
        [form, yearRange],
    );
    const dirty = !!form && serialize(form) !== initialKey;
    const valid = Object.keys(errors).length === 0;

    const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const handleMethodChange = async (opt) => {
        const wasOther = form?.paymentMethod?.label === 'Other';
        const isOther = opt?.label === 'Other';
        setForm((prev) => {
            const next = { ...prev, paymentMethod: opt };
            if (!isChequeOrPdc(opt?.label)) {
                next.chequeNo = '';
                next.chequeDate = null;
                next.drawnBank = '';
            }
            return next;
        });
        if (!opt || wasOther === isOther) return;
        try {
            const items = await fetchAccounts(opt.label);
            setAccountList(items);
            // Same as the create page: the account list depends on the method, so re-pick the account.
            setForm((prev) => {
                const keep = items.find((a) => a.value === prev.bankAccount?.value?.value);
                return { ...prev, bankAccount: keep ? { value: keep, label: keep.text } : null };
            });
        } catch (error) {
            console.error('Error fetching accounts:', error);
        }
    };

    const requestClose = () => {
        if (saving) return;
        if (!viewOnly && dirty) setConfirm('discard');
        else onClose(false);
    };

    const requestSave = () => {
        if (saving || !dirty || !valid) return;
        setConfirm('save');
    };

    const doSave = async () => {
        if (saving) return;
        setConfirm(null);
        setSaving(true);
        setServerError('');
        const cheque = isChequeOrPdc(form.paymentMethod?.label);
        const body = {
            financialYear: header.financialYear,
            postingDate: toApiDate(form.voucherDate),
            bankAccountCode: form.bankAccount?.value?.value,
            paymentMethod: form.paymentMethod?.label,
            chequeNo: cheque ? emptyToNull(form.chequeNo) : null,
            chequeDate: cheque ? toApiDate(form.chequeDate) : null,
            drawnBank: cheque ? emptyToNull(form.drawnBank) : null,
            refNo: emptyToNull(form.refNo),
            paidTo: emptyToNull(form.paidTo),
            narration: emptyToNull(form.narration),
        };
        const res = await apiService.updateBalanceRefund(header.voucherNo, body);
        setSaving(false);
        if (res.ok) {
            toast.success(`Refund voucher ${header.voucherNo} updated successfully`);
            onClose(true);
            return;
        }
        setServerError(res.message);
        if (res.status === 400 && res.message === APPROVED_MESSAGE) {
            setViewOnly(true);
            setBanner(NOW_APPROVED_BANNER);
            load({ keepBanner: true });
        }
    };

    const readOnly = viewOnly;
    const totals = (detail?.postingLines || []).reduce(
        (t, l) => ({ debit: t.debit + (Number(l.debit) || 0), credit: t.credit + (Number(l.credit) || 0) }),
        { debit: 0, credit: 0 },
    );

    const renderBody = () => {
        if (loading) return <Skeleton />;
        if (loadError) return <div className="alert alert-danger mb-0">{loadError}</div>;
        return (
            <>
                {banner && <div className="alert alert-warning">{banner}</div>}
                {serverError && <div className="alert alert-danger">{serverError}</div>}

                <div className="border-box2 p-3 mb-3">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 15 }}>
                        <ReadOnly label="Voucher No" value={header.voucherNo} />
                        <ReadOnly label="Financial Year" value={header.financialYear} />
                        <ReadOnly label="Voucher Type" value={header.voucherType} />
                        <ReadOnly label="Status"><StatusBadge approved={header.isApproved} /></ReadOnly>
                        <ReadOnly label="Created By" value={header.createdBy} />
                        <ReadOnly label="Created On" value={header.createdOn ? moment(header.createdOn).format('DD-MMM-YYYY HH:mm') : ''} />
                    </div>
                </div>

                <div className="border-box2 p-3 mb-3">
                    <h6 className="mainInvHead">Lease</h6>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 15 }}>
                        <ReadOnly label="Lease No" value={header.leaseNo} />
                        <ReadOnly label="Property" value={header.propertyName || header.propertyNo} />
                        <ReadOnly label="Unit" value={header.unitNo} />
                        <ReadOnly label="Tenant No" value={header.tenantNo} />
                        <ReadOnly label="Tenant Name" value={header.tenantName} />
                        <div>
                            <ReadOnly label="Amount" value={fmt3(header.amount)} />
                            <div className="text-muted small mt-1">{LOCKED_NOTE}</div>
                        </div>
                    </div>
                </div>

                <div className="border-box2 p-3 mb-3">
                    <h6 className="mainInvHead">Payment</h6>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 15 }}>
                        <div>
                            <label className="form-label">Voucher Date</label>
                            <DatePicker
                                selected={form.voucherDate}
                                onChange={(d) => setField('voucherDate', d)}
                                dateFormat="dd-MMM-yyyy"
                                className="p-2 form-control w-100"
                                disabled={readOnly}
                                popperProps={{ strategy: 'fixed' }}
                            />
                            <FieldError message={!readOnly && errors.voucherDate} />
                        </div>
                        <div>
                            <label className="form-label">Bank / Cash Account</label>
                            <WindowedSelect
                                className="p-0"
                                theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                menuPortalTarget={document.body}
                                menuPlacement="auto"
                                styles={selectStyles}
                                isDisabled={readOnly}
                                isClearable
                                value={form.bankAccount}
                                onChange={(opt) => setField('bankAccount', opt)}
                                options={accountList.map((a) => ({ value: a, label: a.text }))}
                            />
                            <FieldError message={!readOnly && errors.bankAccount} />
                        </div>
                        <div>
                            <label className="form-label">Payment Method</label>
                            <Select
                                className="p-0"
                                theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                                menuPortalTarget={document.body}
                                menuPlacement="auto"
                                styles={selectStyles}
                                isDisabled={readOnly}
                                isClearable
                                value={form.paymentMethod}
                                onChange={handleMethodChange}
                                options={methodList.map((m) => ({ value: m, label: m.text }))}
                            />
                            <FieldError message={!readOnly && errors.paymentMethod} />
                        </div>
                        {chequeVisible && (
                            <>
                                <div>
                                    <label className="form-label">Cheque No</label>
                                    <input
                                        type="text"
                                        className="p-2 form-control"
                                        maxLength={MAX.chequeNo}
                                        disabled={readOnly}
                                        value={form.chequeNo}
                                        onChange={(e) => setField('chequeNo', e.target.value)}
                                    />
                                    <FieldError message={!readOnly && errors.chequeNo} />
                                </div>
                                <div>
                                    <label className="form-label">Cheque Date</label>
                                    <DatePicker
                                        selected={form.chequeDate}
                                        onChange={(d) => setField('chequeDate', d)}
                                        dateFormat="dd-MMM-yyyy"
                                        className="p-2 form-control w-100"
                                        disabled={readOnly}
                                        popperProps={{ strategy: 'fixed' }}
                                    />
                                    <FieldError message={!readOnly && errors.chequeDate} />
                                </div>
                                <div>
                                    <label className="form-label">Drawn Bank</label>
                                    <input
                                        type="text"
                                        className="p-2 form-control"
                                        maxLength={MAX.drawnBank}
                                        disabled={readOnly}
                                        value={form.drawnBank}
                                        onChange={(e) => setField('drawnBank', e.target.value)}
                                    />
                                    <FieldError message={!readOnly && errors.drawnBank} />
                                </div>
                            </>
                        )}
                        <div>
                            <label className="form-label">Ref No</label>
                            <input
                                type="text"
                                className="p-2 form-control"
                                maxLength={MAX.refNo}
                                disabled={readOnly}
                                value={form.refNo}
                                onChange={(e) => setField('refNo', e.target.value)}
                            />
                            <FieldError message={!readOnly && errors.refNo} />
                        </div>
                        <div>
                            <label className="form-label">Paid To</label>
                            <input
                                type="text"
                                className="p-2 form-control"
                                maxLength={MAX.paidTo}
                                disabled={readOnly}
                                value={form.paidTo}
                                onChange={(e) => setField('paidTo', e.target.value)}
                            />
                            <FieldError message={!readOnly && errors.paidTo} />
                        </div>
                    </div>
                    <div className="mt-3">
                        <label className="form-label">Narration</label>
                        <textarea
                            className="p-2 form-control"
                            rows={3}
                            maxLength={MAX.narration}
                            disabled={readOnly}
                            value={form.narration}
                            onChange={(e) => setField('narration', e.target.value)}
                        />
                        <FieldError message={!readOnly && errors.narration} />
                    </div>
                </div>

                <div className="border-box2 p-3">
                    <div className="d-flex cursor-pointer" onClick={() => setAccountingOpen((o) => !o)} role="button">
                        <h6 className="mainInvHead col mb-0">Accounting</h6>
                        <span>{accountingOpen ? '▲' : '▼'}</span>
                    </div>
                    {accountingOpen && (
                        <div className="mt-3">
                            <div className="table-container">
                                <table className="table table-bordered gridshad mb-2">
                                    <thead className="dynoTable">
                                        <tr>
                                            <th>Account Code</th>
                                            <th>Account Name</th>
                                            <th className="text-end">Debit</th>
                                            <th className="text-end">Credit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(detail.postingLines || []).map((l, i) => (
                                            <tr key={`${l.accountCode}-${i}`}>
                                                <td>{l.accountCode}</td>
                                                <td>{l.accountName}</td>
                                                <td className="text-end">{fmt3(l.debit)}</td>
                                                <td className="text-end">{fmt3(l.credit)}</td>
                                            </tr>
                                        ))}
                                        <tr className="fw-bold">
                                            <td colSpan={2} className="text-end">Total</td>
                                            <td className="text-end">{fmt3(totals.debit)}</td>
                                            <td className="text-end">{fmt3(totals.credit)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            {detail.sdAdjustment && (
                                <div className="text-muted small">
                                    Security deposit adjustment journal {detail.sdAdjustment.voucherNo} dated {fmtDate(detail.sdAdjustment.postingDate)} for {fmt3(detail.sdAdjustment.amount)}. It is not changed by editing this voucher.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </>
        );
    };

    return (
        <>
            <Modal show onHide={requestClose} size="xl" backdrop="static" keyboard={false} scrollable centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {viewOnly ? 'View' : 'Edit'} Refund Voucher {voucherNo}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>{renderBody()}</Modal.Body>
                <Modal.Footer>
                    {loading || loadError || viewOnly ? (
                        <button type="button" className="btn btn-secondary" onClick={() => onClose(false)}>Close</button>
                    ) : (
                        <>
                            <button type="button" className="btn btn-secondary" onClick={requestClose} disabled={saving}>Cancel</button>
                            <button type="button" className="btn btn-primary" onClick={requestSave} disabled={saving || !dirty || !valid}>
                                {saving ? <ClipLoader color="#ffffff" size={16} /> : 'Save'}
                            </button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>

            <Modal show={confirm !== null} onHide={() => setConfirm(null)} backdrop="static" size="sm" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{confirm === 'discard' ? 'Discard changes' : 'Confirm Update'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {confirm === 'discard' ? 'Discard changes?' : `Update refund voucher ${voucherNo}?`}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary" onClick={() => setConfirm(null)}>No</button>
                    {confirm === 'discard' ? (
                        <button type="button" className="btn btn-danger" onClick={() => onClose(false)}>Yes</button>
                    ) : (
                        <button type="button" className="btn btn-primary" onClick={doSave}>Yes</button>
                    )}
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default LeaseBalanceRefundEdit;
